import type DateRange from '../../lib/dateRange';
import { type Measure, type MetricSeriesResult } from '../../types';
import { executeBatchedQuery, processBatchResults, setupBatchCalculation } from './core';

/**
 * Batch calculation for count measures across multiple reporting periods.
 * Reduces query count from N to 1 query for N reporting periods.
 */

/**
 * Calculates count measures using batch queries for multiple reporting periods.
 * This is the main optimization that reduces N queries to 1 query.
 */
export const calculateBatchedCountMeasures = async (
  measure: Measure | null,
  reportingPeriods: DateRange[],
): Promise<Omit<MetricSeriesResult, 'date'>[]> => {
  const setup = setupBatchCalculation(measure, reportingPeriods);

  if ('shouldReturn' in setup) {
    return reportingPeriods.map(() => ({ errors: { ...setup.errors }, result: { value: null } }));
  }

  const {
    errors,
    adjustedReportingPeriods,
    column,
    dateColumn,
    calculationWhereClause,
    periodSize,
    dataProvider,
    fiscalYearStartMonth,
  } = setup;

  try {
    // Execute single batched query using core function
    const batchData = await executeBatchedQuery(
      dataProvider,
      column,
      dateColumn,
      calculationWhereClause,
      adjustedReportingPeriods,
      periodSize,
      'count',
      fiscalYearStartMonth,
    );

    return processBatchResults(
      batchData,
      adjustedReportingPeriods,
      periodSize,
      fiscalYearStartMonth,
      errors,
      'count',
    );
  } catch (error) {
    console.warn('Batch count calculation failed:', error);
    throw error;
  }
};
